import { QueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { ASSET_SLOT_QUERY_KEY, ASSET_SLOT_URL, EVENT_BY_ID_QUERY_KEY } from '@/constants';
import { apiUrl } from '@/helpers';
import { AssetSlot, AssetSlotCreate, AssetSlotUpdate, OrderableItem } from '@/types';

import { MutationOptions, QueryOptions, useMutation, useQuery } from './service';

const api = {
  get: (id: number) => axios.get(`${apiUrl()}${ASSET_SLOT_URL}/${id}`),
  create: (payload: AssetSlotCreate) => axios.post(`${apiUrl()}${ASSET_SLOT_URL}`, payload),
  update: (payload: AssetSlotUpdate) => axios.put(`${apiUrl()}${ASSET_SLOT_URL}`, payload),
  delete: (id: number) => axios.delete(`${apiUrl()}${ASSET_SLOT_URL}/${id}`),
  updateOrder: (payload: OrderableItem[]) =>
    axios.patch(`${apiUrl()}${ASSET_SLOT_URL}/updateOrder`, payload)
};

export const useGetAssetSlotQuery = (id: number | undefined, options?: QueryOptions<AssetSlot>) => {
  return useQuery<AssetSlot>([ASSET_SLOT_QUERY_KEY, id], () => api.get(id as number), {
    enabled: !!id,
    ...options
  });
};

export const useCreateAssetSlotMutation = (
  queryClient: QueryClient,
  options?: MutationOptions<AssetSlotCreate, AssetSlot>
) => {
  return useMutation(api.create, {
    ...options,
    queryClient,
    invalidateQueries: [[EVENT_BY_ID_QUERY_KEY]]
  });
};

export const useUpdateAssetSlotMutation = (
  queryClient: QueryClient,
  options?: MutationOptions<AssetSlotUpdate, AssetSlot>
) => {
  return useMutation(api.update, {
    ...options,
    queryClient,
    invalidateQueries: [[ASSET_SLOT_QUERY_KEY]]
  });
};

export const useDeleteAssetSlotMutation = (
  queryClient: QueryClient,
  options?: MutationOptions<number, unknown>
) => {
  return useMutation(api.delete, {
    ...options,
    queryClient,
    invalidateQueries: [[EVENT_BY_ID_QUERY_KEY]]
  });
};

export const useUpdateAssetSlotOrderMutation = (
  queryClient: QueryClient,
  options?: MutationOptions<OrderableItem[], unknown>
) => {
  return useMutation(api.updateOrder, {
    ...options,
    queryClient,
    invalidateQueries: [[ASSET_SLOT_QUERY_KEY]]
  });
};
